/*
 * Opening a thread, from wherever the reader asks for one (UX §4.5).
 *
 * Five surfaces open the same pane — the hover bar's *Reply in thread*, the row menu, `Enter` or
 * `t` on the active row, the root's footer button, and a reply's permalink — and every one of them
 * goes through `openThread`. The address is part of it: a thread lives at `?thread=<rootId>` on the
 * conversation it belongs to, so a reload, the browser's back button and a link somebody pasted all
 * land on the same screen.
 *
 * The one thing a plain function cannot do — navigate and ask the shell for the details column —
 * belongs to the conversation's route, which installs it here for as long as it is mounted
 * (`routes.tsx`). With nothing installed the call does nothing rather than throwing: a row rendered
 * outside the chat route, in a unit test or in a future surface, is not a crash.
 */

import type { DetailsRequest } from "../../../core/shell/index.ts"
import type { ConversationId } from "../../../core/store/model.ts"

/** The registered `detailsViews` id of the thread pane; a request names it, not the component. */
export const THREAD_VIEW = "chat.thread"

/** The search parameter that carries an open thread, on every conversation route. */
export const THREAD_PARAM = "thread"

/** What the conversation's route lends this module: a thread put into the address and the column. */
export type ThreadOpener = (
	conv: ConversationId,
	rootId: number,
	highlightSeq: number | undefined,
) => void

let opener: ThreadOpener | null = null

/** Installed by the chat route while it is mounted; cleared when it is not. */
export function setThreadOpener(next: ThreadOpener | null): void {
	opener = next
}

/**
 * Shows the thread of `rootId`. `highlightSeq` is the reply a permalink named: the pane scrolls to
 * it and flashes it, the way the timeline flashes a message a permalink named.
 */
export function openThread(
	conv: ConversationId,
	rootId: number,
	opts?: { highlightSeq?: number },
): void {
	opener?.(conv, rootId, opts?.highlightSeq)
}

/** The details request one open thread is, so the route and the pane agree on its parameters. */
export function threadRequest(
	conv: ConversationId,
	rootId: number,
	highlightSeq?: number,
): DetailsRequest {
	return {
		view: THREAD_VIEW,
		params:
			highlightSeq === undefined || highlightSeq === 0
				? { conv, rootId }
				: { conv, rootId, highlight: highlightSeq },
	}
}

/** The other direction: what the pane reads out of the request the shell hands it. */
export function readThreadParams(params: DetailsRequest["params"]): {
	conv: ConversationId
	rootId: number
	highlightSeq: number
} {
	return {
		conv: Number(params["conv"] ?? 0),
		rootId: Number(params["rootId"] ?? 0),
		highlightSeq: Number(params["highlight"] ?? 0),
	}
}

/** Which thread an open details request names, or nothing when it is not a thread at all. */
export function openThreadRoot(request: DetailsRequest | null): number | undefined {
	if (request === null || request.view !== THREAD_VIEW) return undefined
	const rootId = Number(request.params["rootId"] ?? 0)
	return rootId > 0 ? rootId : undefined
}

/**
 * The `?thread=` of one address, as a positive message id or nothing. It is the route's
 * `validateSearch` and the route's own reader, so `?thread=-1` and `?thread=x` are the same as no
 * parameter at all rather than a request for a thread that cannot exist.
 */
export function threadInSearch(search: unknown): number | undefined {
	if (typeof search !== "object" || search === null) return undefined
	const value = Number((search as Record<string, unknown>)[THREAD_PARAM])
	if (!Number.isSafeInteger(value) || value <= 0) return undefined
	return value
}

/**
 * The same address with a thread put into it or taken out of it, every other parameter kept. The
 * route replaces rather than pushes: a thread opened and closed again must not leave the reader
 * pressing Back through a pane that is not there any more.
 */
export function hrefWithThread(pathname: string, searchStr: string, rootId: number | null): string {
	const params = new URLSearchParams(searchStr)
	if (rootId === null) params.delete(THREAD_PARAM)
	else params.set(THREAD_PARAM, String(rootId))
	const rest = params.toString()
	return rest === "" ? pathname : `${pathname}?${rest}`
}

/**
 * What a resolved permalink means for the pane: a seq that belongs to a reply opens its thread with
 * the reply highlighted, and anything else is the timeline's own business (UX §4.5).
 */
export function threadTargetOf(
	found: { seq: number; threadRootId?: number } | null,
): { rootId: number; highlightSeq: number } | null {
	if (found === null || found.threadRootId === undefined) return null
	return { rootId: found.threadRootId, highlightSeq: found.seq }
}
